import { utcToZonedTime } from 'date-fns-tz';
import {
  getDay, getMonth, getYear,
  setHours, setMinutes, setSeconds, setMilliseconds,
  format
} from 'date-fns';
// Import getUserTimezone and convertFromTimezone from centralized module
import { getUserTimezone, convertFromTimezone as convertFromTimezoneModule } from '../modules/timezone';

// Re-export convertFromTimezone for external use
export const convertFromTimezone = convertFromTimezoneModule;

/**
 * Converts a date to a specific timezone
 * @param date The date to convert
 * @param timezone The timezone to convert to (e.g., 'America/New_York')
 * @returns The date in the specified timezone
 */
export const convertToTimezone = (date: Date, timezone: string): Date => {
  try {
    const result = utcToZonedTime(date, timezone);
    return result;
  } catch (error) {
    return date; // Return original date as fallback
  }
};


/**
 * Gets the day of week (0-6, where 0 is Sunday) for a date in a specific timezone
 * @param date The date to get the day of week for
 * @param timezone The timezone to use for the calculation
 * @returns The day of week (0-6)
 */
export const getDayOfWeekInTimezone = (date: Date, timezone: string): number => {
  const zonedDate = convertToTimezone(date, timezone);
  return getDay(zonedDate);
};

/**
 * Creates a date object at midnight (00:00:00) for the specified date in the given timezone
 * @param date The date to set to midnight
 * @param timezone The timezone to use
 * @returns A new date object set to midnight in the specified timezone
 */
export const createMidnightDate = (date: Date, timezone: string): Date => {
  // First convert to the target timezone
  const zonedDate = convertToTimezone(date, timezone);
  
  // Set the time to midnight (00:00:00.000) in that timezone
  const midnightDate = setMilliseconds(
    setSeconds(
      setMinutes(
        setHours(zonedDate, 0),
        0
      ),
      0
    ),
    0
  );
  
  return midnightDate;
};

/**
 * Formats a date in a specific timezone
 * @param date The date to format
 * @param formatStr The date-fns format string
 * @param timezone The timezone to format the date in
 * @returns The formatted date string
 */
export const formatInTimezone = (date: Date, formatStr: string, timezone: string): string => {
  try {
    const zonedDate = convertToTimezone(date, timezone);
    return format(zonedDate, formatStr);
  } catch (error) {
    return format(date, formatStr); // Fallback to local timezone
  }
};

/**
 * Checks if two dates represent the same day in a specific timezone
 * @param date1 The first date
 * @param date2 The second date
 * @param timezone The timezone to use for the comparison
 * @returns True if the dates represent the same day in the specified timezone
 */
export const isSameDayInTimezone = (date1: Date, date2: Date, timezone: string): boolean => {
  const zonedDate1 = convertToTimezone(date1, timezone);
  const zonedDate2 = convertToTimezone(date2, timezone);
  
  return (
    getYear(zonedDate1) === getYear(zonedDate2) &&
    getMonth(zonedDate1) === getMonth(zonedDate2) &&
    zonedDate1.getDate() === zonedDate2.getDate()
  );
};


/**
 * Logs timezone debug information for a date
 * @param date The date to log information for
 * @param meetingTimezone The meeting timezone
 * @param label Optional label for the log
 */
export const logTimezoneDebug = (date: Date, meetingTimezone: string, label = 'Timezone Debug'): void => {
  // No-op: debug logging removed
};

/**
 * Prepares meeting data for creation or update, ensuring proper timezone handling
 * @param meetingData The meeting data to prepare
 * @param creatorTimezone The timezone of the meeting creator
 * @returns The prepared meeting data with proper timezone handling
 */
export const prepareMeetingDataForStorage = (meetingData: any, creatorTimezone: string): any => {
  // Clone the data to avoid modifying the original
  const preparedData = { ...meetingData };
  
  // Determine which timezone value to use, with fallbacks
  const effectiveTimezone = meetingData.timezone || meetingData.timeZone || creatorTimezone;
  
  // Ensure both timezone formats are set consistently
  preparedData.timezone = effectiveTimezone;
  preparedData.timeZone = effectiveTimezone;
  
  // If there's availability schedule data, ensure times are properly handled
  if (preparedData.availabilitySchedule && Array.isArray(preparedData.availabilitySchedule)) {
    preparedData.availabilitySchedule = preparedData.availabilitySchedule.map(schedule => {
      // Clone the schedule to avoid modifying the original
      const newSchedule = { ...schedule };
      
      // If there are specific date-based availabilities (not just day of week),
      // convert them to UTC for storage
      if (newSchedule.specificDates && Array.isArray(newSchedule.specificDates)) {
        newSchedule.specificDates = newSchedule.specificDates.map(dateInfo => {
          if (dateInfo.date) {
            // Convert the date from the meeting timezone to UTC for storage
            const dateInMeetingTz = new Date(dateInfo.date);
            const dateInUTC = convertFromTimezoneModule(dateInMeetingTz, effectiveTimezone);
            return {
              ...dateInfo,
              date: dateInUTC.toISOString()
            };
          }
          return dateInfo;
        });
      }
      
      return newSchedule;
    });
  }
  
  return preparedData;
};

/**
 * Prepares meeting data retrieved from storage for display or use in the UI
 * @param meetingData The meeting data retrieved from storage
 * @param targetTimezone The timezone to convert dates to (usually user's timezone)
 * @returns The prepared meeting data with dates converted to the target timezone
 */
export const prepareMeetingDataFromStorage = (meetingData: any, targetTimezone: string): any => {
  if (!meetingData) return null;
  
  // Clone the data to avoid modifying the original
  const preparedData = { ...meetingData };
  
  // Determine which timezone format is available, with fallback to UTC
  const meetingTimezone = preparedData.timezone || preparedData.timeZone || 'UTC';
  
  // Ensure both timezone formats are set consistently in returned data
  preparedData.timezone = meetingTimezone;
  preparedData.timeZone = meetingTimezone;
  
  // If there's availability schedule data with specific dates, convert them from UTC to target timezone
  if (preparedData.availabilitySchedule && Array.isArray(preparedData.availabilitySchedule)) {
    preparedData.availabilitySchedule = preparedData.availabilitySchedule.map(schedule => {
      // Clone the schedule to avoid modifying the original
      const newSchedule = { ...schedule };
      
      // If there are specific date-based availabilities (not just day of week),
      // convert them from UTC to the target timezone
      if (newSchedule.specificDates && Array.isArray(newSchedule.specificDates)) {
        newSchedule.specificDates = newSchedule.specificDates.map(dateInfo => {
          if (dateInfo.date) {
            // Convert the date from UTC to the target timezone
            const dateInUTC = new Date(dateInfo.date);
            const dateInTargetTz = convertToTimezone(dateInUTC, targetTimezone);
            return {
              ...dateInfo,
              date: dateInTargetTz.toISOString()
            };
          }
          return dateInfo;
        });
      }
      
      return newSchedule;
    });
  }
  
  return preparedData;
}; 